import React, { useState, useEffect } from "react";
import Select from "react-select";
import AddItemButton from "../../common/components/base/AddItemButton";
import ItemsGrid from "../../common/components/widgets/ItemsGrid";
import LoadingSpinner from "../../common/components/LoadingSpinner/LoadingSpinner";
import Swal from "sweetalert2";
import { useRegions } from "../../hooks/useRegions";
import { useJobProfilesByRegion } from "../../hooks/useJobProfilesByRegion";
import { useSkillDimensionsByJobProfile } from "../../hooks/useSkillDimensionsByJobProfile";
import { useDeleteSkillDimension } from "../../hooks/useDeleteSkillDimension";
import UnitOfCompetenceModal from "./UnitOfCompetenceModal";

const columns = ["name", "professionalQualification", "region", "childrenCount", "action"];
const columnsName = ["Name", "Professional Qualification", "Region", "Indicators (n.)", "Actions"];

const UnitsOfCompetenceTab = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedJobProfile, setSelectedJobProfile] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { data: regionsData } = useRegions();
  const { data: jobProfilesData } = useJobProfilesByRegion(selectedRegion?.value);
  const { data: dimensionsData, isFetching: loading } = useSkillDimensionsByJobProfile(
    selectedJobProfile?.value
  );
  const deleteMutation = useDeleteSkillDimension();

  const regions = regionsData?.regions || [];
  const jobProfiles = jobProfilesData?.jobProfiles || [];
  const dimensions = dimensionsData?.skillDimensions || [];

  const regionOptions = regions.map((r) => ({
    value: r.key || r._id,
    label: r.name,
  }));

  const jobProfileOptions = jobProfiles.map((jp) => ({
    value: jp.key || jp._id,
    label: jp.name,
  }));

  useEffect(() => {
    setSelectedJobProfile(null);
  }, [selectedRegion]);

  const items = dimensions.map((d) => ({
    ...d,
    professionalQualification: selectedJobProfile?.label || "",
    region: selectedRegion?.label || "",
    childrenCount: d.children?.length || 0,
  }));

  const handleCreate = () => {
    setSelectedItem(null);
    setIsUpdate(false);
    setOpenModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsUpdate(true);
    setOpenModal(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure you want to delete this Unit of Competence?",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.value === true) {
        deleteMutation.mutate(
          { id: item.key || item._id },
          {
            onSuccess: () => {
              Swal.fire({
                icon: "success",
                title: "Unit of Competence deleted!",
                showConfirmButton: false,
                timer: 1500,
              });
            },
            onError: (error) => {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || error.message || "Cannot delete: item may be referenced",
                showConfirmButton: true,
              });
            },
          }
        );
      }
    });
  };

  return (
    <div>
      <AddItemButton OnClick={handleCreate} disabled={openModal} />

      <div className="row" style={{ marginBottom: "16px", marginTop: "16px" }}>
        <div className="col-sm-12 col-md-4">
          <Select
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value)}
            options={regionOptions}
            isClearable
            placeholder="Select Region"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Select
            value={selectedJobProfile}
            onChange={(value) => setSelectedJobProfile(value)}
            options={jobProfileOptions}
            isClearable
            isDisabled={!selectedRegion}
            placeholder="Select Professional Qualification"
          />
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {selectedRegion && selectedJobProfile && !loading && (
        <ItemsGrid
          columns={columns}
          columnsName={columnsName}
          items={items}
          edit={(index, value) => handleEdit(value)}
          remove={(index, value) => handleDelete(value)}
          title="Units of Competence"
          rowsPerPage={10}
          pagination={true}
        />
      )}

      <UnitOfCompetenceModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        isUpdate={isUpdate}
        item={selectedItem}
        defaultRegion={selectedRegion}
        defaultJobProfile={selectedJobProfile}
      />
    </div>
  );
};

export default UnitsOfCompetenceTab;
